import { IconButton, TextButton } from "@anna-money/anna-web-ui";
import { Autocomplete } from "@react-google-maps/api";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import type { ChangeEvent, FC, ReactElement } from "react";
import { useState } from "react";
import { Form } from "../../../../components/form/form";
import { Input } from "../../../../components/input/input";
import { Toggle } from "../../../../components/toggle/toggle";
import { useFactory } from "../../../../helpers/reactHooks";
import type { PaymentPageForm } from "../../../../types/paymentPage";
import type { FormSectionType } from "../../obPaymentPageForm";
import { EditFormModalSection } from "../editFormSection/editFormModalSection";
import { MapComponent } from "./mapComponent/mapComponent";
import styles from "./businessLocationSection.module.scss";

type LocationFields = Pick<PaymentPageForm, "switchedOnBusinessLocation" | "address" | "googleMapsPlaceId">;
// Both the page form and the modal form are rendered by renderFormContent
type LocationForm = Pick<Form<LocationFields>, "getValue" | "onChange" | "onMultipleChange">;

export const BusinessLocationSection: FC<FormSectionType> = observer(({ form, store }) => {
  const { switchedOnBusinessLocation, address, googleMapsPlaceId } = form.values;
  const modalForm = useFactory(
    () => new Form<LocationFields>({ switchedOnBusinessLocation, address, googleMapsPlaceId }),
  );
  const [editMode, setEditMode] = useState(false);
  const [changeAddress, setChangeAddress] = useState(false);

  const [input, setInput] = useState("");
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);

  const onPlaceChanged = (formData: LocationForm): void => {
    if (autocomplete === null) {
      console.warn("Autocomplete is not loaded yet!");
      return;
    }
    const place = autocomplete.getPlace();
    formData.onChange("googleMapsPlaceId", place.place_id);
    formData.onChange("address", place.formatted_address);
    setInput(place.formatted_address ?? "");
  };

  const renderViewContent = (): ReactElement => (
    <div className={styles.viewSection}>
      <div className={styles.businessLocationSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionViewTitle}>
            <span className={styles.sectionSubtitle}>Business location</span>
          </div>
          <IconButton
            title="Edit business location"
            icon="actionEdit"
            iconColor="themeGreen"
            size="s"
            onClick={() => {
              setEditMode(true);
            }}
          />
        </div>

        <span className={styles.address}>{address}</span>
        <MapComponent placeId={form.getValue("googleMapsPlaceId")} />
      </div>
    </div>
  );

  const handleChangeToggle = (value: boolean, formData: LocationForm): void => {
    formData.onChange("switchedOnBusinessLocation", value);
    if (!value) {
      formData.onMultipleChange([
        { field: "googleMapsPlaceId", value: null },
        { field: "address", value: null },
      ]);
    }
  };

  const renderFormContent = (formData: LocationForm, isModal: boolean): ReactElement => (
    <div className={classNames(styles.section, styles.withSeparator)}>
      <div className={styles.businessLocationSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionSubtitle}>Business location</span>
            <span className={styles.sectionDescription}>
              Include a map of your business location{" "}
              {formData.getValue("address") && "We’ve got your trading address as:"}
            </span>
          </div>
          <Toggle
            label="Business location"
            value={formData.getValue("switchedOnBusinessLocation")}
            onChange={(value: boolean) => {
              handleChangeToggle(value, formData);
            }}
          />
        </div>

        {formData.getValue("switchedOnBusinessLocation") && (
          <>
            {changeAddress ? (
              <Autocomplete
                className={styles.autocomplete}
                onLoad={setAutocomplete}
                onPlaceChanged={() => {
                  onPlaceChanged(formData);
                }}
              >
                <Input
                  className={styles.field}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setInput(e.target.value);
                  }}
                  value={input}
                />
              </Autocomplete>
            ) : (
              <>
                <span className={classNames(styles.address, isModal && styles.textBold)}>
                  {formData.getValue("address")}
                </span>
                <TextButton
                  size="l"
                  text="Change address"
                  className={styles.changeButton}
                  onClick={() => {
                    setChangeAddress(true);
                  }}
                />
              </>
            )}
            <MapComponent placeId={formData.getValue("googleMapsPlaceId")} />
          </>
        )}
      </div>
    </div>
  );

  const handleCancelEditModal = (): void => {
    setEditMode(false);
    modalForm.onMultipleChange([
      { field: "address", value: form.getValue("address") },
      { field: "googleMapsPlaceId", value: form.getValue("googleMapsPlaceId") },
      { field: "switchedOnBusinessLocation", value: form.getValue("switchedOnBusinessLocation") },
    ]);
  };

  const handleSaveEditModal = (): void => {
    setEditMode(false);
    form.onMultipleChange([
      { field: "address", value: modalForm.getValue("address") },
      { field: "googleMapsPlaceId", value: modalForm.getValue("googleMapsPlaceId") },
      { field: "switchedOnBusinessLocation", value: modalForm.getValue("switchedOnBusinessLocation") },
    ]);
  };

  if (store.firstUse || !form.getValue("address")) {
    return renderFormContent(form, false);
  }

  return (
    <>
      {renderViewContent()}
      <EditFormModalSection open={editMode} onCancel={handleCancelEditModal} onSave={handleSaveEditModal}>
        {renderFormContent(modalForm, true)}
      </EditFormModalSection>
    </>
  );
});
